import React, { useEffect, useState } from "react";
import PolymerModal from "./PolymerModal";
import SolventModal from "./SolventModal";
import AdditiveModal from "./AdditiveModal";
import {
    additivesText, CHANNEL_LABELS, channelsForNeedle, mixedConcentration, polymersText, solventsText, stockLabel,
} from "../solutionText";

/**
 * How the spun solution was prepared, in one place: what was dissolved in what
 * (polymers, solvents, additives, concentration), whether polymers were
 * dissolved separately and then mixed, and the properties measured on it.
 *
 * With a coaxial or triaxial needle, each channel (core, middle, shell) can
 * get its own solution, described the same way.
 *
 * Saves either the simple setup (one solution, everything dissolved together:
 * the flat polymer/solvent/solution fields) or `solutions` (a mix, or one
 * solution per channel), with the flat fields as an overview.
 */
const PREPARATIONS = [
    {
        key: "DISSOLVED_TOGETHER", icon: "bi-cup-straw", label: "Dissolved together",
        hint: "One solution: all polymers and solvents together (also a blend dissolved in one solvent system).",
    },
    {
        key: "MIXED", icon: "bi-shuffle", label: "Mixed from separate solutions",
        hint: "Each polymer dissolved in its own solvent first, then mixed, e.g. PCL in DCM + gelatin in HFIP.",
    },
];

const PROPERTY_FIELDS = [
    { key: "viscosity", label: "Viscosity", unitKey: "viscosityUnit", unit: "cP" },
    { key: "surfaceTension", label: "Surface tension", unitKey: "surfaceTensionUnit", unit: "mN/m" },
    { key: "conductivity", label: "Conductivity", unitKey: "conductivityUnit", unit: "mS/cm" },
    { key: "evaporationRate", label: "Evaporation rate", unitKey: "evaporationRateUnit", unit: "mg/cm²/hr" },
];

const isBlank = (v) => v === "" || v === null || v === undefined;
const toNumber = (v) => (isBlank(v) ? null : Number(v));

const emptyStock = () => ({ polymers: [], solvents: [], additives: [], concentration: "", concentrationUnit: "wt%", share: "" });

const propertiesFrom = (sol = {}) => ({
    viscosity: sol.viscosity ?? "", viscosityUnit: sol.viscosityUnit || "cP",
    surfaceTension: sol.surfaceTension ?? "", surfaceTensionUnit: sol.surfaceTensionUnit || "mN/m",
    conductivity: sol.conductivity ?? "", conductivityUnit: sol.conductivityUnit || "mS/cm",
    evaporationRate: sol.evaporationRate ?? "", evaporationRateUnit: sol.evaporationRateUnit || "mg/cm²/hr",
    PH: sol.PH ?? sol.pH ?? "",
});

const measuredFrom = (properties) => ({
    ...Object.fromEntries(PROPERTY_FIELDS.flatMap(f => [[f.key, toNumber(properties[f.key])], [f.unitKey, properties[f.unitKey]]])),
    PH: toNumber(properties.PH),
});

/** One feed (what flows through one channel) as edited here. */
const feedFrom = (feed, channel, properties) => ({
    channel,
    preparation: feed?.stocks?.length > 1 ? "MIXED" : "DISSOLVED_TOGETHER",
    mixRatioUnit: feed?.mixRatioUnit || "v/v",
    stocks: feed?.stocks?.length ? feed.stocks.map(s => ({
        label: s.label || "",
        polymers: s.polymers || [], solvents: s.solvents || [], additives: s.additives || [],
        concentration: s.concentration ?? "", concentrationUnit: s.concentrationUnit || "wt%",
        share: s.share ?? "",
    })) : [emptyStock()],
    properties: propertiesFrom(properties || feed?.properties || {}),
});

/** The setup's single solution (dissolved together, or one MAIN mix). */
function mainFeedFrom(setup) {
    const main = (setup.solutions || []).find(f => !f.channel || f.channel === "MAIN");
    if (main) return feedFrom(main, "MAIN", setup.solutionProperty);
    const sol = setup.solutionProperty || {};
    return feedFrom({
        stocks: [{
            polymers: setup.polymerProperty?.polymerComponents || [],
            solvents: setup.solventProperty?.solventComponents || [],
            additives: setup.additives || [],
            concentration: sol.concentration, concentrationUnit: sol.concentrationUnit,
        }],
    }, "MAIN", sol);
}

export default function SolutionPreparationModal({ setup, onClose, onSave }) {
    const needleChannels = channelsForNeedle(setup.needleProperty?.needleType);
    const storedChannelFeeds = (setup.solutions || []).filter(f => f.channel && f.channel !== "MAIN");

    // Channel solutions are described separately when the needle has channels and
    // the contributor chooses to (always, for a new setup; existing ones keep their form).
    const [separate, setSeparate] = useState(() => needleChannels.length > 0
        && (storedChannelFeeds.length > 0 || !(setup.polymerProperty?.polymerComponents?.length)));
    const [feeds, setFeeds] = useState(() => {
        if (needleChannels.length > 0) {
            const main = mainFeedFrom(setup);
            return needleChannels.map((channel, i) => {
                const stored = storedChannelFeeds.find(f => f.channel === channel);
                if (stored) return feedFrom(stored, channel);
                // Without stored channel solutions, the shell starts from the one solution described so far
                return i === needleChannels.length - 1 && !storedChannelFeeds.length ? { ...main, channel } : feedFrom(null, channel);
            });
        }
        if (storedChannelFeeds.length > 0) {
            // The needle is no longer coaxial: keep the core solution as the solution
            return [{ ...feedFrom(storedChannelFeeds[0], "MAIN"), properties: propertiesFrom(storedChannelFeeds[0].properties || {}) }];
        }
        return [mainFeedFrom(setup)];
    });
    const [mainFeed, setMainFeed] = useState(() => mainFeedFrom(setup)); // the single solution, when not separate
    const [active, setActive] = useState(0);
    const [child, setChild] = useState(null); // { type: 'polymer' | 'solvent' | 'additives', feed, stock }
    const [errors, setErrors] = useState({}); // feedIndex -> { stocks: [], properties: {}, form }
    const [formError, setFormError] = useState(null);

    const droppedChannels = needleChannels.length === 0 && storedChannelFeeds.length > 0;
    const editing = separate ? feeds : [needleChannels.length > 0 ? mainFeed : feeds[0]];
    const setEditing = (index, updater) => {
        if (separate) setFeeds(prev => prev.map((f, i) => (i === index ? updater(f) : f)));
        else if (needleChannels.length > 0) setMainFeed(prev => updater(prev));
        else setFeeds(prev => [updater(prev[0])]);
    };
    const feedIndex = separate ? Math.min(active, editing.length - 1) : 0;
    const feed = editing[feedIndex];
    const mixed = feed.preparation === "MIXED";

    const updateStock = (fIndex, sIndex, patch) => {
        setEditing(fIndex, f => ({ ...f, stocks: f.stocks.map((s, i) => (i === sIndex ? { ...s, ...patch } : s)) }));
        setErrors(prev => ({ ...prev, [fIndex]: { ...(prev[fIndex] || {}), stocks: [] } }));
        setFormError(null);
    };

    const choosePreparation = (next) => {
        if (next === feed.preparation) return;
        if (next === "DISSOLVED_TOGETHER") {
            const filled = feed.stocks.filter(s => s.polymers.length || s.solvents.length);
            if (filled.length > 1 && !window.confirm("Keep only Solution A and remove the other solutions?")) return;
        }
        setEditing(feedIndex, f => ({
            ...f,
            preparation: next,
            stocks: next === "MIXED" ? (f.stocks.length >= 2 ? f.stocks : [...f.stocks, emptyStock()]) : [f.stocks[0] || emptyStock()],
        }));
        setErrors(prev => ({ ...prev, [feedIndex]: {} }));
        setFormError(null);
    };

    const validateFeed = (f) => {
        const isChannel = f.channel !== "MAIN";
        const isMixed = f.preparation === "MIXED";
        const stocks = f.stocks.map(s => {
            const e = {};
            const hasPolymer = s.polymers.some(p => p?.polymerName);
            if (!hasPolymer && !isChannel) e.polymers = "Add the polymer(s).";
            if (!s.solvents.some(v => v?.solventName)) e.solvents = "Add the solvent(s).";
            if (isBlank(s.concentration)) { if (hasPolymer) e.concentration = "Concentration is required."; }
            else if (!(Number(s.concentration) > 0) || Number(s.concentration) > 100) e.concentration = "Between 0 and 100 wt%.";
            if (isMixed) {
                if (isBlank(s.share)) e.share = "Share is required.";
                else if (!(Number(s.share) > 0) || Number(s.share) > 100) e.share = "Above 0 and at most 100.";
            }
            return e;
        });
        let form = null;
        if (isMixed && f.stocks.length < 2) form = "A mix has at least two solutions.";
        if (isMixed && !stocks.some(e => e.share)) {
            const sum = f.stocks.reduce((t, s) => t + Number(s.share), 0);
            if (Math.abs(sum - 100) > 1) form = `The shares add up to ${Math.round(sum * 100) / 100}, not 100.`;
        }
        const properties = {};
        PROPERTY_FIELDS.forEach(p => {
            if (!isBlank(f.properties[p.key]) && !(Number(f.properties[p.key]) > 0)) properties[p.key] = "Must be greater than zero.";
        });
        if (!isBlank(f.properties.PH) && (Number(f.properties.PH) < 0 || Number(f.properties.PH) > 14)) properties.PH = "pH is between 0 and 14.";
        const ok = !form && stocks.every(e => Object.keys(e).length === 0) && Object.keys(properties).length === 0;
        return { ok, stocks, properties, form };
    };

    const validate = () => {
        const results = editing.map(validateFeed);
        setErrors(Object.fromEntries(results.map((r, i) => [i, r])));
        let problem = null;
        if (separate && !editing.some(f => f.stocks.some(s => s.polymers.some(p => p?.polymerName)))) {
            problem = "Add the polymer(s) of at least one channel.";
        }
        setFormError(problem);
        const firstBad = results.findIndex(r => !r.ok);
        if (separate && firstBad >= 0) setActive(firstBad);
        return !problem && firstBad < 0;
    };

    const stocksOut = (f) => f.stocks.map((s, i) => ({
        label: f.preparation === "MIXED" ? (s.label || stockLabel(s, i)) : null,
        polymers: s.polymers, solvents: s.solvents, additives: s.additives,
        concentration: toNumber(s.concentration), concentrationUnit: isBlank(s.concentration) ? null : (s.concentrationUnit || "wt%"),
        share: f.preparation === "MIXED" ? toNumber(s.share) : null,
    }));

    const overview = (all) => ({
        polymerProperty: { ...(setup.polymerProperty || {}), polymerComponents: all.flatMap(f => f.stocks.flatMap(s => s.polymers)) },
        solventProperty: { ...(setup.solventProperty || {}), solventComponents: all.flatMap(f => f.stocks.flatMap(s => s.solvents)) },
        additives: all.flatMap(f => f.stocks.flatMap(s => s.additives)),
    });

    const save = () => {
        if (!validate()) return;
        if (separate) {
            onSave({
                solutions: feeds.map(f => ({
                    channel: f.channel,
                    preparation: f.preparation,
                    mixRatioUnit: f.preparation === "MIXED" ? f.mixRatioUnit : null,
                    stocks: stocksOut(f),
                    properties: measuredFrom(f.properties),
                })),
                ...overview(feeds),
                solutionProperty: {}, // each channel has its own concentration and properties
            });
        } else {
            const f = editing[0];
            const measured = measuredFrom(f.properties);
            if (f.preparation !== "MIXED") {
                const s = f.stocks[0];
                onSave({
                    solutions: null,
                    ...overview([f]),
                    solutionProperty: { ...measured, concentration: toNumber(s.concentration), concentrationUnit: s.concentrationUnit || "wt%" },
                });
            } else {
                const estimate = mixedConcentration(f.stocks, f.mixRatioUnit);
                onSave({
                    solutions: [{ channel: "MAIN", preparation: "MIXED", mixRatioUnit: f.mixRatioUnit, stocks: stocksOut(f) }],
                    ...overview([f]),
                    solutionProperty: { ...measured, concentration: estimate?.value ?? null, concentrationUnit: estimate ? "wt%" : null },
                });
            }
        }
        onClose();
    };

    // Esc closes and Enter saves, unless a dialog for one part is open on top.
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (child || (e.target.id || "").startsWith("react-select")) return;
            if (e.key === "Escape") {
                e.preventDefault();
                onClose();
            } else if (e.key === "Enter" && e.target.tagName !== "TEXTAREA" && e.target.tagName !== "BUTTON") {
                e.preventDefault();
                save();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    });

    const feedErrors = errors[feedIndex] || {};
    const stockErrors = feedErrors.stocks || [];
    const propertyErrors = feedErrors.properties || {};
    const estimate = mixed ? mixedConcentration(feed.stocks, feed.mixRatioUnit) : null;
    const isChannel = separate;
    const where = isChannel ? CHANNEL_LABELS[feed.channel] : null;

    const partRow = (sIndex, type, label, text, required, error) => (
        <div className={`d-flex align-items-start justify-content-between gap-2 py-2 border-bottom ${error ? "text-danger" : ""}`}>
            <div className="small" style={{ minWidth: 0 }}>
                <div className="fw-bold text-secondary text-uppercase" style={{ fontSize: '0.75rem' }}>
                    {label} {required && <span className="text-danger">*</span>}
                </div>
                <div className={text ? "text-dark" : "text-muted"} style={{ overflowWrap: 'anywhere' }}>{text || (required ? "Not added yet" : "None")}</div>
                {error && <div className="small"><i className="bi bi-exclamation-circle me-1"></i>{error}</div>}
            </div>
            <button type="button" className="btn btn-sm btn-outline-primary flex-shrink-0"
                aria-label={`Edit ${label.toLowerCase()} of ${mixed ? `solution ${stockLabel(feed.stocks[sIndex], sIndex)}` : "the solution"}${where ? ` (${where.toLowerCase()})` : ""}`}
                onClick={() => setChild({ type, feed: feedIndex, stock: sIndex })}>
                <i className="bi bi-pencil me-1"></i>Edit
            </button>
        </div>
    );

    const polymerKind = (polymers) => {
        const named = polymers.filter(p => p?.polymerName);
        if (named.length > 1) return "blend";
        if (named.length === 1 && (named[0].copolymer || named[0].newPolymer?.polymerType === "COPOLYMER")) return "copolymer";
        return null;
    };

    const channelSummary = (f) => {
        const texts = f.stocks.map(s => polymersText(s.polymers) || solventsText(s.solvents)).filter(Boolean);
        return texts.length ? texts.join(" + ") : "not described yet";
    };

    const childStocks = child ? editing[child.feed].stocks[child.stock] : null;

    return (
        <>
            <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} role="dialog" aria-labelledby="solution-prep-title">
                <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-xl">
                    <form className="modal-content shadow-lg border-0" onSubmit={(e) => { e.preventDefault(); save(); }}>
                        <div className="modal-header bg-light">
                            <h5 className="modal-title fw-bold text-dark" id="solution-prep-title">
                                <i className="bi bi-droplet-half me-2 text-primary"></i>
                                Solution Preparation
                            </h5>
                            <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
                        </div>

                        <div className="modal-body p-4 bg-light-subtle">
                            {droppedChannels && (
                                <div className="alert alert-warning small">
                                    <i className="bi bi-exclamation-triangle me-1"></i>
                                    The needle is no longer coaxial or triaxial, so only one solution is described. The core solution was kept: please check it.
                                </div>
                            )}
                            {needleChannels.length > 0 && (
                                <div className="d-flex flex-wrap align-items-center gap-3 mb-3 p-3 rounded border bg-white">
                                    <div className="form-check form-switch mb-0">
                                        <input className="form-check-input" type="checkbox" role="switch" id="separate-channels"
                                            checked={separate} onChange={(e) => { setSeparate(e.target.checked); setActive(0); setErrors({}); setFormError(null); }} />
                                        <label className="form-check-label fw-bold" htmlFor="separate-channels">
                                            Describe the {needleChannels.map(c => CHANNEL_LABELS[c].toLowerCase()).join(", ")} solutions separately
                                        </label>
                                    </div>
                                    <span className="small text-muted">
                                        {setup.needleProperty?.needleType} needle. {separate
                                            ? "Each channel's flow rate is entered per run in the table."
                                            : "Off: one solution is described for the whole needle."}
                                    </span>
                                </div>
                            )}

                            {separate && (
                                <ul className="nav nav-tabs mb-3" role="tablist">
                                    {feeds.map((f, i) => (
                                        <li className="nav-item" key={f.channel} role="presentation">
                                            <button type="button" role="tab" aria-selected={i === feedIndex}
                                                className={`nav-link ${i === feedIndex ? "active fw-bold" : ""} ${errors[i] && !errors[i].ok ? "text-danger" : ""}`}
                                                onClick={() => setActive(i)}>
                                                {CHANNEL_LABELS[f.channel]}
                                                <span className="d-block small fw-normal text-muted text-truncate" style={{ maxWidth: '14rem' }}>{channelSummary(f)}</span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}

                            <div className="fw-bold text-secondary mb-2">
                                How was the {where ? `${where.toLowerCase()} solution` : "spun solution"} prepared?
                            </div>
                            <div className="row g-2 mb-4" role="radiogroup" aria-label="Preparation">
                                {PREPARATIONS.map(p => (
                                    <div className="col-md-6" key={p.key}>
                                        <button type="button" role="radio" aria-checked={feed.preparation === p.key}
                                            className="cx-choice"
                                            onClick={() => choosePreparation(p.key)}>
                                            <span className="cx-choice__title"><i className={`bi ${p.icon}`} aria-hidden="true"></i>{p.label}</span>
                                            <span className="cx-choice__hint">{p.hint}</span>
                                        </button>
                                    </div>
                                ))}
                            </div>

                            <div className="row g-3">
                                {feed.stocks.map((stock, index) => {
                                    const err = stockErrors[index] || {};
                                    const kind = polymerKind(stock.polymers);
                                    return (
                                        <div key={`${feedIndex}-${index}`} className={mixed ? "col-lg-6" : "col-12"}>
                                            <div className="card border-light shadow-sm h-100">
                                                <div className="card-header bg-white d-flex justify-content-between align-items-center py-2">
                                                    <span className="fw-bold text-primary">
                                                        {mixed ? `Solution ${stockLabel(stock, index)}` : where ? `The ${where.toLowerCase()} solution` : "The solution"}
                                                        {kind && <span className="badge bg-primary-subtle text-primary-emphasis border ms-2">{kind}</span>}
                                                    </span>
                                                    {mixed && feed.stocks.length > 2 && (
                                                        <button type="button" className="btn btn-outline-danger btn-sm rounded-pill"
                                                            onClick={() => setEditing(feedIndex, f => ({ ...f, stocks: f.stocks.filter((_, i) => i !== index) }))}>
                                                            <i className="bi bi-trash me-1"></i>Remove
                                                        </button>
                                                    )}
                                                </div>
                                                <div className="card-body py-2">
                                                    {partRow(index, "polymer", "Polymers", polymersText(stock.polymers), !isChannel, err.polymers)}
                                                    {partRow(index, "solvent", "Solvents", solventsText(stock.solvents), true, err.solvents)}
                                                    {partRow(index, "additives", "Additives", additivesText(stock.additives), false, null)}
                                                    <div className="row g-2 pt-3">
                                                        <div className={mixed ? "col-6" : "col-md-4"}>
                                                            <label className="form-label small fw-bold text-secondary mb-1" htmlFor={`stock-conc-${feedIndex}-${index}`}>
                                                                Polymer concentration {(!isChannel || stock.polymers.length > 0) && <span className="text-danger">*</span>}
                                                            </label>
                                                            <div className="input-group input-group-sm">
                                                                <input id={`stock-conc-${feedIndex}-${index}`} type="number" step="any" min="0" max="100"
                                                                    className={`form-control ${err.concentration ? "is-invalid" : ""}`}
                                                                    value={stock.concentration} placeholder="e.g. 10"
                                                                    onChange={(e) => updateStock(feedIndex, index, { concentration: e.target.value })} />
                                                                <span className="input-group-text">wt%</span>
                                                            </div>
                                                            {err.concentration && <div className="text-danger small">{err.concentration}</div>}
                                                        </div>
                                                        {mixed && (
                                                            <div className="col-6">
                                                                <label className="form-label small fw-bold text-secondary mb-1" htmlFor={`stock-share-${feedIndex}-${index}`}>
                                                                    Share in the mix <span className="text-danger">*</span>
                                                                </label>
                                                                <div className="input-group input-group-sm">
                                                                    <input id={`stock-share-${feedIndex}-${index}`} type="number" step="any" min="0" max="100"
                                                                        className={`form-control ${err.share ? "is-invalid" : ""}`}
                                                                        value={stock.share} placeholder="e.g. 70"
                                                                        onChange={(e) => updateStock(feedIndex, index, { share: e.target.value })} />
                                                                    <span className="input-group-text">{feed.mixRatioUnit}</span>
                                                                </div>
                                                                {err.share && <div className="text-danger small">{err.share}</div>}
                                                            </div>
                                                        )}
                                                    </div>
                                                    {isChannel && !mixed && (
                                                        <div className="small text-muted mt-2">A channel may carry no polymer, e.g. a solvent sheath or a drug solution in the core.</div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {mixed && (
                                <div className="mt-3">
                                    <button type="button" className="btn btn-outline-primary w-100 py-2" style={{ borderStyle: 'dashed' }}
                                        onClick={() => setEditing(feedIndex, f => ({ ...f, stocks: [...f.stocks, emptyStock()] }))}>
                                        <i className="bi bi-plus-lg me-2"></i>Add another solution to the mix
                                    </button>
                                    <div className="d-flex flex-wrap align-items-center gap-3 mt-3">
                                        <label className="small fw-bold text-secondary mb-0" htmlFor="mix-ratio-unit">The solutions were mixed by</label>
                                        <select id="mix-ratio-unit" className="form-select form-select-sm" style={{ width: 'auto' }}
                                            value={feed.mixRatioUnit} onChange={(e) => { const v = e.target.value; setEditing(feedIndex, f => ({ ...f, mixRatioUnit: v })); }}>
                                            <option value="v/v">volume (v/v)</option>
                                            <option value="w/w">weight (w/w)</option>
                                        </select>
                                        {estimate && (
                                            <span className="small text-muted">
                                                <i className="bi bi-calculator me-1"></i>
                                                Polymer in the {where ? `${where.toLowerCase()} ` : "spun "}solution: {estimate.estimated ? "≈ " : ""}<strong>{estimate.value} wt%</strong>
                                                {estimate.estimated && " (assuming the solutions have equal densities)"}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                            {(feedErrors.form || formError) && (
                                <div className="alert alert-danger py-2 small mt-3 mb-0">
                                    <i className="bi bi-exclamation-circle me-1"></i>{feedErrors.form || formError}
                                </div>
                            )}

                            <div className="card border-light shadow-sm mt-4">
                                <div className="card-header bg-white py-2">
                                    <span className="fw-bold text-secondary">Measured on the {where ? `${where.toLowerCase()} solution` : "spun solution"}</span>
                                    <span className="small text-muted ms-2">optional</span>
                                </div>
                                <div className="card-body">
                                    <div className="row g-3">
                                        {PROPERTY_FIELDS.map(f => (
                                            <div className="col-md-4 col-lg" key={f.key}>
                                                <label className="form-label small fw-bold text-secondary mb-1" htmlFor={`prop-${f.key}`}>{f.label}</label>
                                                <div className="input-group input-group-sm">
                                                    <input id={`prop-${f.key}`} type="number" step="any" min="0"
                                                        className={`form-control ${propertyErrors[f.key] ? "is-invalid" : ""}`}
                                                        value={feed.properties[f.key]}
                                                        onChange={(e) => { const v = e.target.value; setEditing(feedIndex, fd => ({ ...fd, properties: { ...fd.properties, [f.key]: v } })); }} />
                                                    <span className="input-group-text">{feed.properties[f.unitKey] || f.unit}</span>
                                                </div>
                                                {propertyErrors[f.key] && <div className="text-danger small">{propertyErrors[f.key]}</div>}
                                            </div>
                                        ))}
                                        <div className="col-md-4 col-lg">
                                            <label className="form-label small fw-bold text-secondary mb-1" htmlFor="prop-ph">pH</label>
                                            <input id="prop-ph" type="number" step="any" min="0" max="14"
                                                className={`form-control form-control-sm ${propertyErrors.PH ? "is-invalid" : ""}`}
                                                value={feed.properties.PH}
                                                onChange={(e) => { const v = e.target.value; setEditing(feedIndex, fd => ({ ...fd, properties: { ...fd.properties, PH: v } })); }} />
                                            {propertyErrors.PH && <div className="text-danger small">{propertyErrors.PH}</div>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="modal-footer bg-light border-top-0 py-3">
                            <button type="button" className="btn btn-outline-secondary px-4 rounded-pill fw-bold me-2" onClick={onClose}>
                                Cancel
                            </button>
                            <button type="submit" className="btn btn-primary px-4 shadow-sm fw-bold rounded-pill">
                                <i className="bi bi-check2-circle me-1"></i>
                                Save Changes
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {child?.type === "polymer" && (
                <PolymerModal polymerList={childStocks.polymers} onClose={() => setChild(null)}
                    onSave={(list) => updateStock(child.feed, child.stock, { polymers: list })} />
            )}
            {child?.type === "solvent" && (
                <SolventModal solventList={childStocks.solvents} onClose={() => setChild(null)}
                    onSave={(list) => updateStock(child.feed, child.stock, { solvents: list })} />
            )}
            {child?.type === "additives" && (
                <AdditiveModal additiveList={childStocks.additives} onClose={() => setChild(null)}
                    onSave={(list) => updateStock(child.feed, child.stock, { additives: list })} />
            )}
        </>
    );
}
